import { GET_USER, GET_USER_GROUP } from 'api/graphql'
import { PlaylistMemberType, UserGroupType, UserType } from 'types'
import { hasPlaylistPermission, idAsNumber } from 'utils'
import styled, { keyframes } from 'styled-components'
import { useEffect, useState } from 'react'

import { File } from 'components/Files/FileSystem'
import FileSystemUsers from 'components/Files/FileSystemUsers'
import { PLAYLIST_ROLES } from 'const'
import PlaylistMembersList from './PlaylistMembersList'
import SvgIcon from 'components/SvgIcon'
import { toast } from 'react-toastify'
import { useQuery } from '@apollo/client'
import { useTranslation } from 'react-i18next'

const Wrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 15px;
`

const ButtonWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 30px;
  margin-top: 30px;
`

const AnimationIn = keyframes`
  0% {
    opacity: 0;
    transform: scale(0);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
`

const AddUserWrapper = styled.div`
  animation-name: ${AnimationIn};
  animation-timing-function: cubic-bezier(0.075, 0.82, 0.165, 1);
  animation-duration: 0.2s;
  animation-fill-mode: backwards;

  align-self: stretch;
  flex: 1;
`

/**
 *
 *  COMPONENT
 *
 */
const PlaylistMembers = (props: {
  playlistMembers: PlaylistMemberType[]
  setPlaylistMembers?: (members: PlaylistMemberType[]) => void
  isEditMode?: boolean
}) => {
  const { t } = useTranslation()

  const [isAddFocused, setIsAddFocused] = useState<boolean>(false)

  const allowUpdatePlaylistMembers =
    !props.isEditMode ||
    hasPlaylistPermission(
      props.playlistMembers,
      'PLAYLIST_updatePlaylistMembers'
    )

  //
  // FETCH SELECTED USER
  //
  const [fetchUserId, setFetchUserId] = useState<number>()

  const { data: fetchedUserData, refetch: refetchUserData } = useQuery(
    GET_USER,
    {
      variables: { id: fetchUserId },
      skip: !fetchUserId,
    }
  )

  useEffect(() => {
    if (fetchedUserData) {
      refetchUserData()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchUserId])

  useEffect(() => {
    const user = fetchedUserData?.getUser
    if (
      user &&
      props.playlistMembers
        .map((m) => idAsNumber(m.user?.id))
        .includes(idAsNumber(user.id))
    ) {
      toast.info(t('playlist.user already a member'))
      return
    }
    if (user) {
      addMemberUser(user)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchedUserData])

  //
  // FETCH SELECTED USER GROUP
  //
  const [fetchUserGroupId, setFetchUserGroupId] = useState<number>()

  const { data: fetchedUserGroupData, refetch: refetchUserGroupData } =
    useQuery(GET_USER_GROUP, {
      variables: { id: fetchUserGroupId },
      skip: !fetchUserGroupId,
    })

  useEffect(() => {
    if (fetchedUserGroupData) {
      refetchUserGroupData()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchUserGroupId])

  useEffect(() => {
    const userGroup = fetchedUserGroupData?.getUserGroup
    if (
      userGroup &&
      props.playlistMembers
        .map((m) => idAsNumber(m.userGroup?.id))
        .includes(idAsNumber(userGroup.id))
    ) {
      toast.info(t('playlist.user group already a member'))
      return
    }
    if (userGroup) {
      addMemberUserGroup(userGroup)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchedUserGroupData])

  //
  // FUNCTIONS
  //
  const addMemberUser = (user: UserType) => {
    props.setPlaylistMembers([
      ...props.playlistMembers,
      {
        user: user,
        playlistRole: {
          code: PLAYLIST_ROLES.member,
        },
      },
    ])
  }

  const addMemberUserGroup = (userGroup: UserGroupType) => {
    props.setPlaylistMembers([
      ...props.playlistMembers,
      {
        userGroup: userGroup,
        playlistRole: {
          code: PLAYLIST_ROLES.member,
        },
      },
    ])
  }

  //
  // FILE MANAGEMENT
  //
  const onUserDoubleClick = (file: File) => {
    if (file.id === fetchUserId) {
      toast.info(t('playlist.user already a member'))
      return
    }
    setFetchUserId(file.id)
  }

  const onUserGroupDoubleClick = (file: File) => {
    if (file.id === fetchUserGroupId) {
      toast.info(t('playlist.user group already a member'))
      return
    }
    setFetchUserGroupId(file.id)
  }

  //
  // RENDER
  //
  return (
    <Wrapper>
      <PlaylistMembersList
        playlistMembers={props.playlistMembers}
        setPlaylistMembers={props.setPlaylistMembers}
        onDeleteCallback={() => {
          setFetchUserId(undefined)
          setFetchUserGroupId(undefined)
        }}
        allowUpdateMembers={allowUpdatePlaylistMembers}
      />

      {props.setPlaylistMembers && allowUpdatePlaylistMembers && (
        <ButtonWrapper>
          {isAddFocused ? (
            <>
              <AddUserWrapper>
                <FileSystemUsers
                  defaultViewMode="grid"
                  onFileAddClick={onUserDoubleClick}
                  onFileDoubleClick={onUserDoubleClick}
                  onFolderAddClick={onUserGroupDoubleClick}
                  onFolderDoubleClick={onUserGroupDoubleClick}
                  maxHeight="40vh"
                />
              </AddUserWrapper>

              <button
                onClick={() => setIsAddFocused(false)}
                title={t('playlist.close add user')}
                className="btn-action-circular"
              >
                <SvgIcon code="icon-close" />
              </button>
            </>
          ) : (
            <button
              onClick={() => setIsAddFocused(true)}
              title={t('playlist.add user')}
              className="btn-action-circular"
            >
              <SvgIcon code="icon-add" />
            </button>
          )}
        </ButtonWrapper>
      )}
    </Wrapper>
  )
}

export default PlaylistMembers
